import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Zap, Loader2 } from "lucide-react";

export function CreditTopUp() {
  const { refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState(5);

  useEffect(() => {
    api.get("/topup/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  // hidden/disabled for non-subscribers
  if (!config || !config.eligible) return null;

  const ratio = config.credits_per_euro || 0;
  const amt = Number(amount);
  const valid = amt >= config.min_amount;
  const credits = valid ? Math.round(amt * ratio) : 0;

  const createOrder = async () => {
    const { data } = await api.post("/paypal/topup/create", { amount: amt });
    return data.order_id;
  };

  const onApprove = async (data) => {
    try {
      const { data: res } = await api.post("/paypal/topup/capture", { order_id: data.orderID });
      await refresh();
      toast.success(`+${res.credits_added} crédits ajoutés ! Solde : ${res.balance}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="credit-topup">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-5 h-5 text-[#3ECF8E]" />
        <h3 className="font-display text-lg font-bold">Recharger des crédits</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Abonnement {config.plan_name} · ≈ {ratio.toFixed(1)} crédits / €
      </p>

      <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Montant (€)</label>
      <div className="relative mt-1.5">
        <Input
          type="number" min={config.min_amount} step="1" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="bg-white/5 border-white/10 h-11 pr-10 text-lg font-semibold"
          data-testid="topup-amount-input"
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">€</span>
      </div>

      <div className="flex items-center justify-between mt-3 rounded-xl bg-lime-400/10 border border-lime-400/25 px-4 py-3">
        <span className="text-sm text-slate-300">Vous recevrez</span>
        <span className="flex items-center gap-1.5 text-lime-300 font-mono text-lg font-bold" data-testid="topup-credits-preview">
          <Coins className="w-4 h-4" /> {credits}
        </span>
      </div>

      {!valid && <p className="text-xs text-red-400 mt-2">Montant minimum : {config.min_amount} €</p>}

      <div className="mt-4" data-testid="topup-paypal-button">
        <PayPalScriptProvider options={{ clientId: config.client_id, currency: "EUR", intent: "capture" }}>
          <PayPalButtons
            disabled={!valid}
            forceReRender={[amt]}
            style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay", height: 44 }}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={() => toast.error("Erreur PayPal. Réessayez.")}
          />
        </PayPalScriptProvider>
      </div>
    </div>
  );
}
